import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
} from "@mui/material";
import type { Subscription } from "../../Graphql/types/subscription";
import { useExtendSubscription } from "../../hooks/api/useSubscription";

interface ExtendSubscriptionModalProps {
  open: boolean;
  onClose: () => void;
  subscription: Subscription | null;
  onSuccess?: () => void;
}

const toDateOnly = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const toISOEndOfDay = (dateStr: string): string => {
  const d = new Date(dateStr);
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
};

const ExtendSubscriptionModal: React.FC<ExtendSubscriptionModalProps> = ({
  open,
  onClose,
  subscription,
  onSuccess,
}) => {
  const [dateValue, setDateValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  const extendSubscription = useExtendSubscription();

  useEffect(() => {
    if (open) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setDateValue(toDateOnly(tomorrow));
      setError(null);
    }
  }, [open]);

  const minDate = (() => {
    const t = new Date();
    t.setDate(t.getDate() + 1);
    return toDateOnly(t);
  })();

  const handleSubmit = async () => {
    if (!subscription) return;
    const chosen = new Date(dateValue);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    chosen.setHours(0, 0, 0, 0);
    if (chosen.getTime() <= today.getTime()) {
      setError("Please choose a future date.");
      return;
    }
    setError(null);
    try {
      await extendSubscription.mutateAsync({
        subscriptionId: subscription.subscriptionId,
        subscriptionEndsAt: toISOEndOfDay(dateValue),
      });
      onSuccess?.();
      onClose();
    } catch {
      // Error handled by fetchGraphQL / setAlert
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold">
          Extend subscription
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Set the new end date for this subscription. Only future dates are
            allowed.
          </Typography>
          <TextField
            label="New end date"
            type="date"
            value={dateValue}
            onChange={(e) => setDateValue(e.target.value)}
            inputProps={{ min: minDate }}
            error={!!error}
            helperText={error}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={extendSubscription.isPending}
        >
          Extend
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExtendSubscriptionModal;
